import React, { useState } from 'react';
import { Search, X, Filter, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AuditLogFilters as FiltersType,
  ENTITY_TYPES,
  ACTIVITY_TYPE_OPTIONS
} from '@/types/auditLog';
import { User } from '@/lib/api';

interface AuditLogFiltersProps {
  filters: FiltersType;
  onFiltersChange: (filters: FiltersType) => void;
  onClear: () => void;
  users?: User[];
  isLoadingUsers?: boolean;
}

export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
  filters,
  onFiltersChange,
  onClear,
  users = [],
  isLoadingUsers = false,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || '');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFiltersChange({ ...filters, search: searchValue });
  };

  const handleFilterChange = (key: keyof FiltersType, value: any) => {
    onFiltersChange({ ...filters, [key]: value || undefined });
  };

  const handleTypeToggle = (type: string) => {
    const currentTypes = filters.types || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter(t => t !== type)
      : [...currentTypes, type];
    onFiltersChange({ ...filters, types: newTypes.length > 0 ? newTypes : undefined });
  };

  const hasActiveFilters = !!(
    filters.userId ||
    filters.entityType ||
    filters.startDate ||
    filters.endDate ||
    filters.search ||
    (filters.types && filters.types.length > 0)
  );

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.userId) count++;
    if (filters.entityType) count++;
    if (filters.startDate || filters.endDate) count++;
    if (filters.search) count++;
    if (filters.types && filters.types.length > 0) count++;
    return count;
  };

  return (
    <div className="space-y-4">
      {/* Main Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search audit logs..."
              className="pl-9 pr-4"
            />
          </div>
        </form>

        {/* Date Range */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* User Filter */}
        <Select
          value={filters.userId || 'all'}
          onValueChange={(value) => handleFilterChange('userId', value === 'all' ? undefined : value)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {isLoadingUsers ? (
              <SelectItem value="" disabled>Loading...</SelectItem>
            ) : (
              users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        {/* Entity Type Filter */}
        <Select
          value={filters.entityType || 'all'}
          onValueChange={(value) => handleFilterChange('entityType', value === 'all' ? undefined : value)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            {ENTITY_TYPES.map((entity) => (
              <SelectItem key={entity.value} value={entity.value}>
                {entity.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Advanced Filters Toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={cn(showAdvanced && 'bg-blue-50 border-blue-200')}
        >
          <Filter className="w-4 h-4 mr-2" />
          Advanced
          {getActiveFilterCount() > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
              {getActiveFilterCount()}
            </span>
          )}
        </Button>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Activity Types</h4>
          <div className="flex flex-wrap gap-2">
            {ACTIVITY_TYPE_OPTIONS.map((option) => {
              const isSelected = filters.types?.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => handleTypeToggle(option.value)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
                    isSelected
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Applied Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Applied:</span>

          {filters.search && (
            <FilterChip
              label={`Search: "${filters.search}"`}
              onRemove={() => {
                setSearchValue('');
                handleFilterChange('search', undefined);
              }}
            />
          )}

          {filters.userId && (
            <FilterChip
              label={`User: ${users.find(u => u.id === filters.userId)?.name || 'Unknown'}`}
              onRemove={() => handleFilterChange('userId', undefined)}
            />
          )}

          {filters.entityType && (
            <FilterChip
              label={`Entity: ${ENTITY_TYPES.find(e => e.value === filters.entityType)?.label}`}
              onRemove={() => handleFilterChange('entityType', undefined)}
            />
          )}

          {(filters.startDate || filters.endDate) && (
            <FilterChip
              label={`Date: ${filters.startDate || 'Start'} - ${filters.endDate || 'End'}`}
              onRemove={() => {
                onFiltersChange({ ...filters, startDate: undefined, endDate: undefined });
              }}
            />
          )}

          {filters.types && filters.types.length > 0 && (
            <FilterChip
              label={`Types: ${filters.types.length} selected`}
              onRemove={() => handleFilterChange('types', undefined)}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Filter Chip Component
const FilterChip: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full">
    {label}
    <button
      onClick={onRemove}
      className="p-0.5 hover:bg-blue-100 rounded-full"
    >
      <X className="w-3 h-3" />
    </button>
  </span>
);
